import { createActorContext } from '@xstate/react';
import React, { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { documentUploadQueueMachine } from './machine';

const DocumentUploadQueueContext = createActorContext(documentUploadQueueMachine);

const beforeUnloadHandler = (e: Event) => {
    e.preventDefault();
    return null;
};

function DocumentUploadQueueProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const queryClient = useQueryClient();

    return (
        <DocumentUploadQueueContext.Provider
            logic={documentUploadQueueMachine.provide({
                actions: {
                    uploadedCompleted: (_, { property }) => {
                        void queryClient.invalidateQueries({
                            queryKey: property.type === 'NON-MANAGED'
                                ? ['non-managed-property', property.id, 'documents']
                                : ['property', property.id, 'documents'],
                        });
                    },
                    queueLengthChanged: (_, { activeCount }) => {
                        if (activeCount !== 0) {
                            window.addEventListener('beforeunload', beforeUnloadHandler);
                        } else {
                            window.removeEventListener('beforeunload', beforeUnloadHandler);
                        }
                    },
                },
            })}
            options={{
                input: {
                    uploadCapacity: 2, // Allow 2 files to be uploaded at once
                    maxFileSizeBytes: 1024 * 1024 * 20, // 20mb
                },
            }}
        >
            { children}
        </DocumentUploadQueueContext.Provider>
    );
}

function useUploadQueueStats() {
    const queuedCount = DocumentUploadQueueContext
        .useSelector((state) => state.context.queue.filter(
            (upload) => upload.getSnapshot().matches('queued')
        ).length);

    const activeCount = DocumentUploadQueueContext
        .useSelector((state) => state.context.queue.filter(
            (upload) => upload.getSnapshot().matches('uploading')
        ).length);

    const failedCount = DocumentUploadQueueContext
        .useSelector((state) => state.context.queue.filter(
            (upload) => upload.getSnapshot().matches('failed')
        ).length);

    const successCount = DocumentUploadQueueContext
        .useSelector((state) => state.context.queue.filter(
            (upload) => upload.getSnapshot().matches('success')
        ).length);

    const hasPendingItems = useMemo(
        () => queuedCount > 0 || activeCount > 0,
        [queuedCount, activeCount]
    );

    return {
        activeCount,
        failedCount,
        successCount,
        queuedCount,
        hasPendingItems,
    };
}

export { DocumentUploadQueueProvider, DocumentUploadQueueContext, useUploadQueueStats };
